import React, { useContext, useEffect, useState } from 'react'
import {
    Container,
    EditIcon,
    LotName,
    Subtitle,
    Title,
    DropdownTitle,
    SelectOverride,
    DatePickerOverrides,
    SpinnerContainer,
    BaseButtonOverrides
} from './Stay.style'
import { useTranslation } from 'react-i18next'
import FlexContainer from '@components/style/FlexContainer.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_SIZE } from 'parkdepot-shared/components/Buttons'
import { useHistory } from 'react-router-dom'
import { carpayLPNotFoundOverviewRoute } from '../route.const'
import { Datepicker, TimePicker } from 'baseui/datepicker'
import useLanguage from '@hooks/useLanguage.hook'
import { BaseSelect } from 'parkdepot-shared/components/Select'
import { Options, Value } from 'baseui/select'
import { defaultValueOption } from '@constants/carpayVehicleTypes.consts'
import { useQuery } from '@apollo/client'
import Spinner from 'parkdepot-shared/components/Spinner'
import B2C_GET_LOT_BY_ID from '@graphql/queries/getLotById.query'
import { ErrorFlowContext } from '@context/ErrorFlowContext/errorFlow.context'
import { getLocale } from '@utils/getLocale.utils'
import { Duration } from 'luxon'
import { LotType } from 'parkdepot-shared/gql/graphql'

const CarpayLPNotFoundStay = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const [lng] = useLanguage()

    const { errorFlow, setErrorFlow } = useContext(ErrorFlowContext)
    const { lot } = errorFlow
    const lotId = lot && lot.id ? lot.id : new URLSearchParams(location.search).get('lotId')

    const [rangeDate, setRangeDate] = useState<any>([new Date(), new Date()])

    const [tariffs, setTariffs] = useState<Options>([])
    const [selectedTariff, setSelectedTariff] = useState<Value>()

    const [vehicleTypes, setVehicleTypes] = useState<Options>([])
    const [selectedVehicleType, setSelectedVehicleType] = useState<Value>([defaultValueOption(t)])

    const { data, loading } = useQuery(B2C_GET_LOT_BY_ID, {
        variables: {
            lot_id: lotId || ''
        },
        skip: !lotId
    })
    const carpayLot = data?.b2c_get_carpay_lot

    const tariffsExist = carpayLot?.lot?.lot_tariffs && carpayLot?.lot?.lot_tariffs?.length > 0
    const contextExist = carpayLot?.parking_contexts && carpayLot?.parking_contexts?.length > 0

    const showTarrifs = tariffsExist && carpayLot.lot.lot_type !== LotType.Hybrid

    // const isPrepaidLot = carpayLot?.lot.lot_type === LotType.Prepaid
    // const isTollRoadLot = carpayLot?.lot.sv_lot_type === SvLotType.TollRoad

    const onEditClick = () => {
        history.goBack()
    }

    const onSubmitClick = () => {
        if (rangeDate[0] === null || rangeDate[1] === null) {
            return
        }
        const start_datetime = rangeDate[0]
        const end_datetime = rangeDate[1]
        const selectedTariffOption = selectedTariff ? (selectedTariff[0].value as string) || null : null
        const selectedContext = selectedVehicleType ? (selectedVehicleType[0].id as string) || null : null

        const endDateTime = selectedTariffOption ? null : end_datetime

        setErrorFlow({
            ...errorFlow,
            start_datetime,
            end_datetime: endDateTime,
            selectedTariff: selectedTariffOption,
            selectedContext
        })

        history.push(carpayLPNotFoundOverviewRoute)
    }

    useEffect(() => {
        if (data) {
            const lotFromApi = data.b2c_get_carpay_lot.lot
            const parkingContexts = data.b2c_get_carpay_lot.parking_contexts

            const durationTariffs = lotFromApi.lot_tariffs.map((tariff) => ({
                value: Duration.fromISO(tariff),
                isValid: Duration.fromISO(tariff).isValid,
                oldValue: tariff
            }))
            const sortedTariffs = durationTariffs.sort(
                (firstTariff, secondTariff) => firstTariff.value.toMillis() - secondTariff.value.toMillis()
            )

            const tariffsInHours = sortedTariffs.map((tariff) => ({
                label: `${tariff.value.toFormat('hh')} ${
                    tariff.value.as('hours') > 1 ? t('common.hours') : t('common.hour')
                }`,
                value: tariff.oldValue
            }))

            setTariffs(tariffsInHours)

            const defaultVehicleType = defaultValueOption(t)

            const vehicleTypes = parkingContexts.map((context) => ({
                label: t(`carpay.paymentInput.vehicleTypes.${context?.vehicle_type?.toLowerCase()}`),
                value: context.uuid,
                id: context.uuid
            }))
            const vehicleTypeOptions = [defaultVehicleType, ...vehicleTypes]
            setVehicleTypes(vehicleTypeOptions)
        }
    }, [data])

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    return (
        <Container>
            <Title>{t('carpay.notFound.stay.title')}</Title>
            <FlexContainer gap="2rem" $fullWidth alignitems="normal">
                <FlexContainer $column gap="0.8rem" $fullWidth alignitems="normal">
                    <Subtitle>{t('carpay.notFound.stay.location')}</Subtitle>
                    <LotName>{carpayLot?.lot?.name}</LotName>
                </FlexContainer>
                <EditIcon onClick={onEditClick} className="wemolo-edit" aria-hidden="true" />
            </FlexContainer>
            <Subtitle>{t('carpay.notFound.stay.parkingStart')}</Subtitle>
            {!showTarrifs && (
                <>
                    <FlexContainer gap="0.4rem" $fullWidth $column alignitems="normal">
                        <DropdownTitle>{t('carpay.notFound.stay.selectDate')}</DropdownTitle>
                        <Datepicker
                            range
                            maxDate={new Date()}
                            locale={getLocale(lng)}
                            value={rangeDate}
                            onChange={({ date }) => {
                                setRangeDate(date)
                            }}
                            formatString="dd.MM.yyyy"
                            overrides={DatePickerOverrides}
                        />
                    </FlexContainer>

                    <FlexContainer gap="1.6rem" $fullWidth>
                        <FlexContainer $column gap="0.4rem" $fullWidth alignitems="normal">
                            <DropdownTitle>{t('carpay.notFound.stay.checkin')}</DropdownTitle>
                            <div id="check-in-input">
                                <TimePicker
                                    value={rangeDate[0]}
                                    format="24"
                                    step={300}
                                    onChange={(time) => {
                                        setRangeDate([time, rangeDate[1]])
                                    }}
                                />
                            </div>
                        </FlexContainer>
                        <FlexContainer $column gap="0.4rem" $fullWidth alignitems="normal">
                            <DropdownTitle>{t('carpay.notFound.stay.checkout')}</DropdownTitle>
                            <div id="check-out-input">
                                <TimePicker
                                    value={rangeDate[1]}
                                    format="24"
                                    step={300}
                                    minTime={rangeDate[0]}
                                    onChange={(time) => {
                                        setRangeDate([rangeDate[0], time])
                                    }}
                                />
                            </div>
                        </FlexContainer>
                    </FlexContainer>
                </>
            )}
            {showTarrifs && (
                // datepicker with time input, no range
                <FlexContainer gap="0.4rem" $fullWidth $column alignitems="normal">
                    <DropdownTitle>{t('carpay.notFound.stay.selectDateAndTime')}</DropdownTitle>
                    <div id="date-and-time-picker">
                        <Datepicker
                            maxDate={new Date()}
                            locale={getLocale(lng)}
                            value={rangeDate[0]}
                            onChange={({ date }) => {
                                setRangeDate([date, date])
                            }}
                            formatString="dd.MM.yyyy HH:mm"
                            timeSelectStart
                            // change start time label
                            overrides={{
                                ...DatePickerOverrides
                            }}
                        />
                    </div>
                </FlexContainer>
            )}

            {showTarrifs && (
                <>
                    <FlexContainer $column gap="0.4rem" alignitems="normal" $fullWidth>
                        <DropdownTitle>{t('carpay.notFound.stay.selectTarif')}</DropdownTitle>
                        <BaseSelect
                            id="tariff-selector"
                            placeholder={t('carpay.notFound.stay.selectTarif')}
                            options={tariffs}
                            valueKey="value"
                            value={selectedTariff}
                            onChange={(newValue) => {
                                setSelectedTariff(newValue.value)
                            }}
                            clearable={false}
                            overrides={SelectOverride}
                        />
                    </FlexContainer>
                </>
            )}

            {contextExist && (
                <>
                    <FlexContainer $column gap="0.4rem" alignitems="normal" $fullWidth>
                        <DropdownTitle>{t('carpay.notFound.stay.vehicleType')}</DropdownTitle>
                        <BaseSelect
                            placeholder={t('carpay.notFound.stay.selectVehicleType')}
                            options={vehicleTypes}
                            valueKey="label"
                            value={selectedVehicleType}
                            onChange={(newValue) => {
                                setSelectedVehicleType(newValue.value)
                            }}
                            clearable={false}
                            overrides={SelectOverride}
                        />
                    </FlexContainer>
                </>
            )}

            <Button
                id="stay-screen-next-button"
                $size={BUTTON_SIZE.medium}
                onClick={onSubmitClick}
                overrides={BaseButtonOverrides}
            >
                {t('carpay.notFound.stay.next')}
            </Button>
        </Container>
    )
}

export default CarpayLPNotFoundStay
